import type { MarketingDraft, MarketingStep, PushContent } from "./types";
import { isMessage } from "./scenario";

export const PUSH_LIMITS = {
  title: 200,
  body: 300,
  subject: 64,
  link: 1024,
} as const;
export const PUSH_STATUS_KEY = ["marketing", "push", "status"] as const;
export const emptyPush = (): PushContent => ({
  title: "",
  body: "",
  subject: "",
  link: "",
});
export const isPushOnly = (steps: MarketingStep[]): boolean => {
  const messages = steps.filter(isMessage);
  return (
    messages.length > 0 && messages.every((step) => step.channel === "push")
  );
};
/** Новому бесплатному запуску плательщик не нужен; существующий счёт сохраняет историю расходов. */
export function normalizePushBilling(
  draft: MarketingDraft,
  preservePayer = false,
): MarketingDraft {
  return isPushOnly(draft.steps)
    ? {
        ...draft,
        payerCompanyId: preservePayer ? draft.payerCompanyId : "",
        budget: "0",
        unlimitedBudget: true,
      }
    : draft;
}
export const pushVariableTexts = (push?: PushContent): string[] =>
  push ? [push.title, push.body, push.link || ""] : [];

export function safePushLink(value: string): boolean {
  if (!value) return true;
  try {
    const url = new URL(value);
    return (
      url.protocol === "https:" &&
      !!url.hostname &&
      !/[{}]/.test(url.hostname) &&
      !url.username &&
      !url.password &&
      !/[\u0000-\u0020\u007f]/.test(value)
    );
  } catch {
    return false;
  }
}

export function validatePush(push?: PushContent): string[] {
  const value = push || emptyPush();
  const errors: string[] = [];
  if (!value.title.trim()) errors.push("заполните заголовок пуша.");
  // Starter ограничивает UTF-16: эмодзи вне BMP занимают две позиции.
  if (value.title.length > PUSH_LIMITS.title)
    errors.push("заголовок пуша — до 200 символов.");
  if (!value.body.trim()) errors.push("заполните текст пуша.");
  if (value.body.length > PUSH_LIMITS.body)
    errors.push("текст пуша — до 300 символов.");
  if (/[\u0000]/.test(value.title + value.body))
    errors.push("уберите недопустимый символ из заголовка или текста пуша.");
  if (!value.subject.trim())
    errors.push("выберите тип уведомления приложения.");
  if (
    value.subject.length > PUSH_LIMITS.subject ||
    /[\r\n\u0000{}]/.test(value.subject)
  )
    errors.push("некорректный тип уведомления приложения.");
  if (new TextEncoder().encode(value.link || "").length > PUSH_LIMITS.link)
    errors.push("ссылка пуша слишком длинная: до 1024 байт.");
  if (!safePushLink(value.link || ""))
    errors.push("укажите HTTPS-ссылку пуша без логина и пароля.");
  return errors;
}

/** Только визуальный пример. Настоящий промокод назначает и подставляет сервер. */
export function personalizePush(
  value: PushContent,
  promo = "",
  name = "Гость",
  dish = "Филадельфия",
  pair = "Калифорния",
): PushContent {
  const variables: Record<string, string> = {
    name,
    имя: name,
    promo,
    промокод: promo,
    favorite_dish: dish || "ваши любимые блюда",
    любимое_блюдо: dish || "ваши любимые блюда",
    pair_dish: pair || "[нет пары]",
    к_нему_берут: pair || "[нет пары]",
  };
  const replace = (text: string, link = false) =>
    text.replace(/\{([^{}]+)\}/g, (token, key: string) =>
      Object.hasOwn(variables, key)
        ? link
          ? encodeURIComponent(variables[key])
          : variables[key]
        : token,
    );
  return {
    ...value,
    title: replace(value.title),
    body: replace(value.body),
    link: replace(value.link || "", true),
  };
}
